import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  JobVariable,
  JobVariableValues,
  JobMatchParams,
} from 'src/types/job-variables';
import * as reduxApi from 'src/utils/redux-api-v2';
import {key, cached, fetching} from 'src/utils/redux';
import {thunkify as flow} from 'src/utils/thunks';
import {pushModal, popModal} from 'src/action-creators/modal';


export const RECEIVE_JOB_VARIABLES = 'job-variables/receiveAll';
export const RECEIVE_JOB_VARIABLE_VALUES = 'job-variables/receiveAllValues';

type ReceiveJobVariablesAction = {
  type: 'job-variables/receiveAll';
  payload: JobVariable[];
};

type ReceiveJobVariableValuesAction = {
  type: 'job-variables/receiveAllValues';
  payload: JobVariableValues;
};

export type JobVariablesAction =
  | ReceiveJobVariablesAction
  | ReceiveJobVariableValuesAction;

const receiveJobVariables = (
  jobVariables: JobVariable[],
): ReceiveJobVariablesAction => ({
  type: RECEIVE_JOB_VARIABLES,
  payload: jobVariables,
});

const receiveJobVariableValues = (
  values: JobVariableValues,
): ReceiveJobVariableValuesAction => ({
  type: RECEIVE_JOB_VARIABLE_VALUES,
  payload: values,
});

export const getAllJobVariables: () => ThunkAction<unknown> = flow(
  key(() => 'job-variables/all'),
  cached((jobVariables) => receiveJobVariables(jobVariables), {
    ttl: 86_400_000,
  }),
  fetching(),
)(
  () => (dispatch: Dispatch) =>
    dispatch(reduxApi.get('job_match/attribute/details')),
);

export const getJobVariableValues: (
  arg1: string,
  arg2: number,
  arg3: JobMatchParams,
) => ThunkAction<unknown> = flow(
  key(
    (
      candidateEntityId: string,
      job_matches_count: number,
      job_matches_params: JobMatchParams,
    ) =>
      `candidateEntityId:${candidateEntityId}:${job_matches_count}:${JSON.stringify(
        job_matches_params,
      )}`,
  ),
  cached((json) => receiveJobVariableValues(json), {
    hash: (
      candidateEntityId: string,
      job_matches_count: number,
      job_matches_params: JobMatchParams,
    ) =>
      `candidateEntityId:${candidateEntityId}:${job_matches_count}:${JSON.stringify(
        job_matches_params,
      )}`,
  }),
  fetching(),
)(
  (
    candidateEntityId: string,
    job_matches_count: number,
    job_matches_params: JobMatchParams,
  ) =>
    reduxApi.get(`job_match/candidate/${candidateEntityId}/data`, {
      num_job_matches: job_matches_count,
      job_matches_params: JSON.stringify(job_matches_params),
    }),
);

export const getJobVariableValuesForAnyEntity: (
  arg1: string,
  arg2: number,
  arg3: JobMatchParams,
  arg4: string,
) => ThunkAction<unknown> = flow(
  key(
    (
      anchorEntityId: string,
      job_matches_count: number,
      job_matches_params: JobMatchParams,
      workflow_entity_type: string,
    ) =>
      `anchorEntityId:${anchorEntityId}:${job_matches_count}:${JSON.stringify(
        job_matches_params,
      )}:workflow_entity_type:${workflow_entity_type}`,
  ),
  cached((json) => receiveJobVariableValues(json), {
    hash: (
      anchorEntityId: string,
      job_matches_count: number,
      job_matches_params: JobMatchParams,
      workflow_entity_type: string,
    ) =>
      `anchorEntityId:${anchorEntityId}:${job_matches_count}:${JSON.stringify(
        job_matches_params,
      )}:workflow_entity_type:${workflow_entity_type}`,
  }),
  fetching(),
)(
  (
    anchorEntityId: string,
    job_matches_count: number,
    job_matches_params: JobMatchParams,
    workflow_entity_type,
  ) =>
    reduxApi.get(
      `job_match/workflow_job_match_data/${workflow_entity_type}/${anchorEntityId}`,
      {
        num_job_matches: job_matches_count,
        job_matches_params: JSON.stringify(job_matches_params),
      },
    ),
);

export const showJobMatchParamsModal: (arg1: {
  dynamicLabels: string[];
  job_matches_count: number;
  job_matches_params: JobMatchParams;
  onParamsChange: (params: JobMatchParams, matchesCount: number) => void;
}) => ThunkAction<unknown> =
  ({dynamicLabels, job_matches_count, job_matches_params, onParamsChange}) =>
  (dispatch: Dispatch): Promise<boolean> =>
    new Promise((resolve: (result: Promise<boolean> | boolean) => void) => {
      // TODO: allow api users to handle confirm and reject
      dispatch(
        pushModal({
          // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'dynamicLabels' does not exist in type 'BasicModal'.
          dynamicLabels,
          job_matches_count,
          job_matches_params: job_matches_params ?? {
            city: null,
            state: null,
            keywords: null,
            title: null,
            skills: null,
          },
          onParamsChange,
          type: 'JOB_MATCH_PARAMS_MODAL',
          confirmText: 'Okay',
          handleConfirm: () => {
            dispatch(popModal());
            resolve(true);
          },
        }),
      );
    });
